import config from '../config';

import type { Account, AuditLogEntry } from './model';

/** Maximum page size allowed by the audit log API; minimizes round trips for export. */
export const AUDIT_LOG_EXPORT_PAGE_LIMIT = 20;
/** Default cap on total entries included in one export file. */
export const AUDIT_LOG_EXPORT_MAX_ENTRIES_DEFAULT = 100;
/** Hard maximum for the “max entries” control (inclusive). */
export const AUDIT_LOG_EXPORT_MAX_ENTRIES_CAP = 1000;
const LOW_REMAINING_THRESHOLD = 5;
/** Max consecutive HTTP 429 responses per page fetch before failing (iterative retry — no recursion / stack growth). */
const AUDIT_LOG_EXPORT_MAX_CONSECUTIVE_429 = 40;
/** LD REST API rate limit headers (`Headers.get` is case-insensitive). See apidocs/content/overview.md. */
const RL_GLOBAL_REMAINING = 'x-ratelimit-global-remaining';
const RL_ROUTE_REMAINING = 'x-ratelimit-route-remaining';
const RL_RESET_MS = 'x-ratelimit-reset';

export function clampAuditLogMaxEntries(raw: number): number {
  if (Number.isNaN(raw) || raw < 1) {
    return AUDIT_LOG_EXPORT_MAX_ENTRIES_DEFAULT;
  }
  return Math.min(AUDIT_LOG_EXPORT_MAX_ENTRIES_CAP, Math.max(1, Math.trunc(raw)));
}

export type AuditLogExportMeta = Readonly<{
  afterEpochMs: number | null;
  beforeEpochMs: number | null;
  limit: number;
  maxEntriesRequested: number;
  truncatedAtMaxEntries: boolean;
  /** True when the user stopped the export (Cancel / AbortSignal). */
  userAborted: boolean;
  spec: string | null;
  query: string | null;
  member: string | null;
  fuzzy: boolean;
  pagesFetched: number;
  totalItems: number;
  exportComplete: boolean;
  partialExport: boolean;
  checkpointSavedAtMs: number;
}>;

export type AuditLogExportPayload = Readonly<{
  meta: AuditLogExportMeta;
  items: AuditLogEntry[];
}>;

type AuditLogPageJson = Readonly<{
  items: unknown[];
  _links?: {
    next?: { href?: string };
  };
}>;

function parseIntHeader(headers: Headers, name: string): number | undefined {
  const raw = headers.get(name);
  if (raw === null || raw === undefined) {
    return undefined;
  }
  const n = Number.parseInt(raw.trim(), 10);
  return Number.isNaN(n) ? undefined : n;
}

function sleepMs(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const cleanup = () => {
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
        timeoutId = undefined;
      }
      signal?.removeEventListener('abort', onAbort);
    };
    const onAbort = () => {
      cleanup();
      reject(new DOMException('Aborted', 'AbortError'));
    };
    timeoutId = setTimeout(() => {
      cleanup();
      resolve();
    }, ms);
    signal?.addEventListener('abort', onAbort);
  });
}

async function rateLimitSleep(headers: Headers, signal?: AbortSignal): Promise<void> {
  const globalRem = parseIntHeader(headers, RL_GLOBAL_REMAINING);
  const routeRem = parseIntHeader(headers, RL_ROUTE_REMAINING);
  const rems = [globalRem, routeRem].filter((r): r is number => r !== undefined);
  if (rems.length === 0 || Math.min(...rems) > LOW_REMAINING_THRESHOLD) {
    return;
  }

  const resetMs = parseIntHeader(headers, RL_RESET_MS);
  if (resetMs === undefined) {
    await sleepMs(1000, signal);
    return;
  }
  const waitMs = Math.max(0, resetMs - Date.now()) + 250;
  if (waitMs > 0) {
    await sleepMs(waitMs, signal);
  }
}

/**
 * Keep pagination `before` from `next` href; re-apply active filters + limit.
 */
export function mergeAuditQuery(
  nextHref: string,
  afterMs: number | undefined,
  spec: string | undefined,
  query: string | undefined,
  member: string | undefined,
  fuzzy: boolean | undefined,
  limit: number,
): string {
  const u = new URL(nextHref, 'https://app.launchdarkly.com');
  const params = new URLSearchParams(u.search);
  if (afterMs !== undefined) {
    params.set('after', String(afterMs));
  } else {
    params.delete('after');
  }
  if (spec) {
    params.set('spec', spec);
  } else {
    params.delete('spec');
  }
  if (query) {
    params.set('q', query);
  } else {
    params.delete('q');
  }
  if (member) {
    params.set('member', member);
  } else {
    params.delete('member');
  }
  if (fuzzy) {
    params.set('fuzzy', 'true');
  } else {
    params.delete('fuzzy');
  }
  params.set('limit', String(limit));
  const path = u.pathname;
  return `${path.startsWith('/') ? path : `/${path}`}?${params.toString()}`;
}

function resolveFetchUrl(pathAndQuery: string): string {
  if (pathAndQuery.startsWith('http://') || pathAndQuery.startsWith('https://')) {
    return pathAndQuery;
  }
  const path = pathAndQuery.startsWith('/') ? pathAndQuery : `/${pathAndQuery}`;
  return `${config.gonfalon.host}${path}`;
}

function buildRequestHeaders(account: Account): HeadersInit {
  return {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'X-LD-AccountId': account._id,
    'X-LD-EnvId': account._id,
    'X-LD-PrjId': account._id,
    ...(config.gonfalon.localPrivateAuth ? { 'X-LD-Private': 'allowed' } : {}),
  };
}

function itemInWindow(item: AuditLogEntry, afterMs: number | undefined, beforeMs: number | undefined): boolean {
  const { date } = item;
  if (typeof date !== 'number') {
    return true;
  }
  const d = Math.trunc(date);
  if (afterMs !== undefined && d <= afterMs) {
    return false;
  }
  if (beforeMs !== undefined && d >= beforeMs) {
    return false;
  }
  return true;
}

function buildPayload(
  afterMs: number | undefined,
  beforeMs: number | undefined,
  limit: number,
  maxEntriesRequested: number,
  truncatedAtMaxEntries: boolean,
  userAborted: boolean,
  spec: string | undefined,
  query: string | undefined,
  member: string | undefined,
  fuzzy: boolean,
  pagesFetched: number,
  items: AuditLogEntry[],
  exportComplete: boolean,
): AuditLogExportPayload {
  return {
    meta: {
      afterEpochMs: afterMs ?? null,
      beforeEpochMs: beforeMs ?? null,
      limit,
      maxEntriesRequested,
      truncatedAtMaxEntries,
      userAborted,
      spec: spec ?? null,
      query: query ?? null,
      member: member ?? null,
      fuzzy,
      pagesFetched,
      totalItems: items.length,
      exportComplete,
      partialExport: !exportComplete,
      checkpointSavedAtMs: Date.now(),
    },
    items,
  };
}

async function fetchAuditLogPage(
  account: Account,
  pathAndQuery: string,
  signal: AbortSignal | undefined,
): Promise<{ data: AuditLogPageJson; headers: Headers }> {
  const url = resolveFetchUrl(pathAndQuery);
  let consecutive429 = 0;
  let res!: Response;

  /* oxlint-disable no-await-in-loop */
  while (true) {
    res = await fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: buildRequestHeaders(account),
      signal,
    });

    if (res.status === 429) {
      consecutive429 += 1;
      if (consecutive429 > AUDIT_LOG_EXPORT_MAX_CONSECUTIVE_429) {
        throw new Error(
          `Audit log export failed (${res.status}): more than ${AUDIT_LOG_EXPORT_MAX_CONSECUTIVE_429} consecutive rate-limit responses; try again later.`,
        );
      }
      const { headers } = res;
      const resetMs = parseIntHeader(headers, RL_RESET_MS);
      const waitMs = resetMs !== undefined ? Math.max(0, resetMs - Date.now()) + 500 : 5000;
      await sleepMs(waitMs, signal);
    } else {
      break;
    }
  }
  /* oxlint-enable no-await-in-loop */

  if (!res.ok) {
    let body = '';
    try {
      body = await res.text();
    } catch {
      /* ignore */
    }
    throw new Error(`Audit log export failed (${res.status}): ${body || res.statusText}`);
  }

  const data = (await res.json()) as AuditLogPageJson;
  const { headers } = res;
  return { data, headers };
}

function isAuditLogEntry(x: unknown): x is AuditLogEntry {
  return typeof x === 'object' && x !== null && typeof (x as AuditLogEntry)._id === 'string';
}

export type FetchAllAuditLogPagesOptions = Readonly<{
  account: Account;
  afterMs?: number;
  beforeMs?: number;
  limit: number;
  /** Stop after this many entries (after date-window filtering). Clamped 1–1000 by the UI. */
  maxEntries: number;
  spec?: string;
  query?: string;
  member?: string;
  fuzzy?: boolean;
  signal?: AbortSignal;
  onProgress?: (message: string) => void;
}>;

/**
 * Paginates GET /private/accounts/:id/auditlog:
 * rate-limit aware, merges active filters into each `next` link, optional client-side date window filter.
 */
export async function fetchAllAuditLogPagesForExport(
  options: FetchAllAuditLogPagesOptions,
): Promise<AuditLogExportPayload> {
  const { account, afterMs, beforeMs, limit, maxEntries, spec, query, member, fuzzy, signal, onProgress } = options;

  const params = new URLSearchParams();
  params.set('limit', String(limit));
  if (afterMs !== undefined) {
    params.set('after', String(afterMs));
  }
  if (beforeMs !== undefined) {
    params.set('before', String(beforeMs));
  }
  if (spec) {
    params.set('spec', spec);
  }
  if (query) {
    params.set('q', query);
  }
  if (member) {
    params.set('member', member);
  }
  if (fuzzy) {
    params.set('fuzzy', 'true');
  }

  let pathAndQuery: string | null = `/private/accounts/${account._id}/auditlog?${params.toString()}`;
  const allItems: AuditLogEntry[] = [];
  let pages = 0;
  let droppedOutOfWindow = 0;
  let exportComplete = false;
  let truncatedAtMaxEntries = false;
  let userAborted = false;

  /* Sequential pagination: one GET per page, rate-limit sleep only before the next request
   * (so stopping mid-run does not discard an already-fetched page). */
  /* oxlint-disable no-await-in-loop */
  try {
    while (pathAndQuery) {
      if (signal?.aborted) {
        userAborted = true;
        exportComplete = false;
        break;
      }

      onProgress?.(`Fetching page ${pages + 1} (${allItems.length} records so far)…`);

      const { data, headers } = await fetchAuditLogPage(account, pathAndQuery, signal);

      const rawItems = data.items;
      if (!Array.isArray(rawItems)) {
        throw new Error('Unexpected response: missing items list');
      }

      pages += 1;
      const nBefore = allItems.length;
      /** True when we stopped before consuming the rest of `rawItems` because `maxEntries` was already reached. */
      let stoppedAtCapWithUnprocessedPageItems = false;
      for (const it of rawItems) {
        if (isAuditLogEntry(it)) {
          if (itemInWindow(it, afterMs, beforeMs)) {
            if (allItems.length >= maxEntries) {
              stoppedAtCapWithUnprocessedPageItems = true;
              break;
            }
            allItems.push(it);
          } else {
            droppedOutOfWindow += 1;
          }
        }
      }
      if (allItems.length > maxEntries) {
        allItems.length = maxEntries;
      }
      const keptPage = allItems.length - nBefore;

      const nextHref = data._links?.next?.href;
      const hasNext = typeof nextHref === 'string' && nextHref.length > 0;

      if (allItems.length >= maxEntries) {
        truncatedAtMaxEntries = hasNext || stoppedAtCapWithUnprocessedPageItems;
        exportComplete = !hasNext && !stoppedAtCapWithUnprocessedPageItems;
        pathAndQuery = null;
        let capProgress: string;
        if (hasNext) {
          capProgress = `Reached max of ${maxEntries} entries (${allItems.length} kept); more pages exist on the API.`;
        } else if (stoppedAtCapWithUnprocessedPageItems) {
          capProgress = `Reached max of ${maxEntries} entries (${allItems.length} kept); more matching records exist on the last page response.`;
        } else {
          capProgress = `Page ${pages}: +${keptPage} kept (${allItems.length} total); done.`;
        }
        onProgress?.(capProgress);
      } else if (hasNext) {
        onProgress?.(`Page ${pages}: +${keptPage} kept (${allItems.length} total); loading next page…`);
        pathAndQuery = mergeAuditQuery(nextHref, afterMs, spec, query, member, fuzzy, limit);
        exportComplete = false;
      } else {
        onProgress?.(`Page ${pages}: +${keptPage} kept (${allItems.length} total); done.`);
        pathAndQuery = null;
        exportComplete = true;
      }

      if (signal?.aborted) {
        userAborted = true;
        exportComplete = false;
        onProgress?.('Stopping after this page — saving collected entries…');
        break;
      }

      if (pathAndQuery) {
        await rateLimitSleep(headers, signal);
      }
    }
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') {
      userAborted = true;
      exportComplete = false;
      truncatedAtMaxEntries = false;
      onProgress?.('Export stopped.');
    } else {
      throw e;
    }
  }
  /* oxlint-enable no-await-in-loop */

  if (droppedOutOfWindow > 0) {
    onProgress?.(`Note: ${droppedOutOfWindow} entries were outside the after/before window and were skipped.`);
  }

  return buildPayload(
    afterMs,
    beforeMs,
    limit,
    maxEntries,
    truncatedAtMaxEntries,
    userAborted,
    spec,
    query,
    member,
    fuzzy ?? false,
    pages,
    allItems,
    exportComplete,
  );
}

export function downloadAuditLogExportJson(payload: AuditLogExportPayload, filename: string): void {
  const text = `${JSON.stringify(payload, null, 2)}\n`;
  const blob = new Blob([text], { type: 'application/json;charset=utf-8' });
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objectUrl);
}
